import { IncludeExcludeToggle } from "@/components/IncludeExcludeToggle";
import { SheetHeader } from "@/design-system/BottomSheet/SheetHeader";
import { Button } from "@/design-system/Button/Button";
import Input from "@/design-system/Input/Input";
import { SectionLabel } from "@/design-system/SectionLabel/SectionLabel";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { Well } from "@/design-system/Well/Well";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { SettingRow } from "@/pages/Sbcs/components/SolveSettings/SettingRow";
import {
  DEFAULT_LEVEL_SETTINGS,
  type GalleryLevelSettings,
  formatAmountInput,
  levelSettingsChanged,
} from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import { Fragment } from "react";

const ASSETS_BASE_URL = import.meta.env.VITE_APP_ASSETS_BASE_URL;

// Each source's include/exclude row with its price icon, and its cap nested under it.
const ROWS = [
  {
    include: "includeObjectivePlayers",
    label: "objectivePlayers",
    icon: "obj-tmp.png",
  },
  {
    include: "includeSbcPlayers",
    label: "sbcPlayers",
    icon: "sbc-tmp.png",
    cap: {
      key: "maxSbcPlayerPrice",
      label: "maxSbcPlayerPrice",
      icon: <CoinIcon size={14} />,
    },
  },
  {
    include: "includeSeasonPlayers",
    label: "seasonPlayers",
    icon: "sp-tmp.png",
    cap: { key: "maxSeasonLevel", label: "maxSeasonLevel", icon: undefined },
  },
  {
    include: "includeSeasonPremium",
    label: "seasonPremium",
    icon: "sp-tmp.png",
    cap: {
      key: "maxPremiumSeasonLevel",
      label: "maxSeasonLevel",
      icon: undefined,
    },
  },
] as const;

/**
 * Customize on the Levels page, laid out like the SBC solve settings: which
 * players the next build may use, each cap nested under its source like the
 * Players tab's Past and Present Clubs. Changes apply at once; Apply closes.
 */
export const GalleryLevelSettingsPanel = ({
  compact,
  close,
  settings,
  onChange,
}: {
  compact: boolean;
  close: () => void;
  settings: GalleryLevelSettings;
  onChange: (settings: GalleryLevelSettings) => void;
}) => {
  const { t } = useTranslation(["gallery", "common"]);
  const set = <K extends keyof GalleryLevelSettings>(
    key: K,
    value: GalleryLevelSettings[K],
  ) => onChange({ ...settings, [key]: value });
  const applyButton = (
    <Button variant="primary" size="small" onClick={close}>
      {t("common:apply")}
    </Button>
  );
  // Back to everything excluded; applies at once like any other change.
  const resetButton = (
    <TextButton
      disabled={!levelSettingsChanged(settings)}
      onClick={() => onChange(DEFAULT_LEVEL_SETTINGS)}
    >
      {t("common:reset")}
    </TextButton>
  );

  return (
    <div className="flex flex-col text-neutral-100">
      {compact && (
        <SheetHeader
          title={t("levels.customize.title")}
          onClose={close}
          closeLabel={t("common:close")}
          action={
            <>
              {resetButton}
              {applyButton}
            </>
          }
        />
      )}
      <section className="flex flex-col gap-4 p-4">
        <SectionLabel>{t("levels.customize.sources")}</SectionLabel>
        <Well className="divide-y divide-neutral-750 overflow-hidden">
          {ROWS.map((row) => {
            const included = settings[row.include];
            return (
              <Fragment key={row.include}>
                <SettingRow
                  label={
                    <span className="flex items-center gap-2.5">
                      <img
                        className="size-5 shrink-0"
                        src={`${ASSETS_BASE_URL}/misc/${row.icon}`}
                        alt=""
                      />
                      {t(`levels.customize.${row.label}`)}
                    </span>
                  }
                  control={
                    <IncludeExcludeToggle
                      variant="pill"
                      value={included ? "include" : "exclude"}
                      onChange={(value) =>
                        set(row.include, value === "include")
                      }
                    />
                  }
                />
                {"cap" in row && (
                  // Like the Players tab's Past and Present Clubs: only while its source is included.
                  <div
                    className={cn(
                      "flex items-center gap-x-3 bg-surface-raised py-2.5 pl-7 pr-3",
                      !included && "pointer-events-none opacity-50",
                    )}
                  >
                    <div className="w-0.5 self-stretch rounded-full bg-control" />
                    <span className="flex-1 text-sm text-foreground-body">
                      {t(`levels.customize.${row.cap.label}`)}
                    </span>
                    {/* Input has no disabled look, and its clear button would still work. */}
                    <span className="w-36 shrink-0">
                      <Input
                        size="small"
                        inputMode="decimal"
                        icon={row.cap.icon}
                        placeholder={t("plan.noLimit")}
                        disabled={!included}
                        value={settings[row.cap.key]}
                        onChange={(v) => set(row.cap.key, formatAmountInput(v))}
                      />
                    </span>
                  </div>
                )}
              </Fragment>
            );
          })}
        </Well>
      </section>
      {!compact && (
        <div className="flex items-center justify-between border-t border-neutral-700 bg-neutral-900 p-4">
          {resetButton}
          {applyButton}
        </div>
      )}
    </div>
  );
};
